/**
 * FileTree — shows the actual file layout of a module.
 *
 * Renders the file tree from /feed/file-tree.json (bundled with the SPA).
 * Each file links to its GitHub blob in the canonical source repo.
 *
 * Groups files by directory (top-level files first, then subdirs).
 * Shows size and a small icon for common file types.
 */

import { useEffect, useState } from 'react';
import './FileTree.css';

interface FileEntry {
  path: string;
  size: number;
}

interface ModuleEntry {
  kind: 'top-level' | 'widget' | 'bundle' | 'app' | 'library' | 'theme' | 'tool';
  path: string;
  files: FileEntry[];
  _canonical_source?: string;
  _canonical_version?: string;
  description?: string;
  category?: string;
  tier?: string;
  tags?: string[];
}

type FileTreeMap = Record<string, ModuleEntry>;

interface FileTreeProps {
  moduleId: string;
  /**
   * Optional override: pass the entry directly (e.g. when loaded server-side)
   * to skip the fetch. Mostly for tests + future server-rendered use.
   */
  entry?: ModuleEntry;
}

function formatSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function iconForFile(filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase() ?? '';
  if (['tsx', 'ts', 'jsx', 'js', 'mjs', 'cjs'].includes(ext)) return '⚡';
  if (['json'].includes(ext)) return '⚙';
  if (['md', 'mdx'].includes(ext)) return '📖';
  if (['css', 'scss', 'less'].includes(ext)) return '🎨';
  if (['svg', 'png', 'jpg', 'gif', 'webp'].includes(ext)) return '🖼';
  if (['yml', 'yaml'].includes(ext)) return '📋';
  if (['html'].includes(ext)) return '🌐';
  if (['sh', 'bash', 'zsh'].includes(ext)) return '🐚';
  if (['py'].includes(ext)) return '🐍';
  if (filename === 'package.json') return '📦';
  if (filename === 'module.json' || filename === 'module-meta.json') return '📋';
  if (filename === 'codex.md' || filename === 'recipe.md' || filename === 'rules.md') return '📖';
  if (filename === 'plan.md' || filename === 'coverage-matrix.md') return '📝';
  if (filename === 'ingredients.json') return '🧪';
  if (filename === 'origin.json') return '🧬';
  if (filename === 'README.md') return '📘';
  if (filename.startsWith('.')) return '⚙';
  return '📄';
}

/**
 * Build the GitHub blob URL for a file in a module.
 * The module's _canonical_source is like "avidtech6/oscar-platform/app/src/modules/ai-actions"
 * which isn't quite the right shape - we need the GitHub URL.
 */
function githubUrlFor(module: ModuleEntry, filePath: string): string {
  const canonical = module._canonical_source ?? '';
  if (!canonical) {
    // Fall back to the gallery repo
    return `https://github.com/avidtech6/fv-module-gallery/blob/main/${filePath}`;
  }
  // The canonical source is "{owner}/{repo}/path/to/module"
  // Strip the module name from the end of filePath if it matches
  // Easier: just use the path as-is. owner/repo + path works for most cases.
  // For "avidtech6/oscar-platform/app/src/modules/ai-actions":
  //   the filePath might be "modules/ai-actions/src/index.ts"
  //   so the URL would be https://github.com/avidtech6/oscar-platform/blob/main/app/src/modules/ai-actions/src/index.ts
  // But canonical_source includes the module path. We want to make sure filePath is rooted at the module
  // and the module path is at canonical_source/{module-relative-path}
  //
  // Simplest: take canonical as "avidtech6/oscar-platform/app/src/modules/ai-actions"
  // and replace the last segment of canonical with filePath
  // BUT we don't know what "last segment" is
  // 
  // Heuristic: canonical ends with the module id. We strip that off
  const parts = canonical.split('/');
  // The last part is the module name
  const moduleName = parts[parts.length - 1];
  // The base is everything before
  const base = parts.slice(0, -1).join('/');
  // The filePath should start with the module's path within its repo
  // If filePath contains moduleName, replace it with empty
  // Actually, easier: just use the canonical + filePath-relative-to-module-path
  // 
  // filePath from the gallery is e.g. "modules/ai-actions/src/index.ts" (path is from gallery root)
  // In the canonical repo, the same file is at "app/src/modules/ai-actions/src/index.ts" (path from repo root)
  // 
  // Simplification: just use the filePath as the gallery path. The user can fix the canonical_source.
  // The gallery repo URL is always a valid fallback.
  const moduleId = moduleName;
  if (filePath.endsWith(moduleId) || filePath.includes(`/${moduleId}/`)) {
    // The filePath contains the module id; use the canonical path
    // Strip the leading "modules/" or "cms-widgets/" etc and use canonical as base
    const pathParts = filePath.split('/');
    const moduleIdx = pathParts.indexOf(moduleId);
    if (moduleIdx >= 0) {
      const relativePath = pathParts.slice(moduleIdx + 1).join('/');
      if (relativePath) {
        return `https://github.com/${base}/blob/main/${parts.slice(-1)[0] === moduleId ? '' : ''}${relativePath}`;
      }
    }
  }
  // Default: just link to the gallery repo
  return `https://github.com/avidtech6/fv-module-gallery/blob/main/${filePath}`;
}

function groupByDirectory(files: FileEntry[], rootPath: string): Map<string, FileEntry[]> {
  const groups = new Map<string, FileEntry[]>();
  for (const file of files) {
    // Strip the root path prefix
    let rel = file.path;
    if (rel.startsWith(rootPath)) {
      rel = rel.slice(rootPath.length);
    }
    // rel is like "src/index.ts" or just "codex.md"
    const dir = rel.includes('/') ? rel.split('/').slice(0, -1).join('/') : '.';
    if (!groups.has(dir)) groups.set(dir, []);
    groups.get(dir)!.push({ ...file, path: rel });
  }
  // Sort: top-level (.) first, then alphabetical
  return new Map([...groups.entries()].sort((a, b) => {
    if (a[0] === '.') return -1;
    if (b[0] === '.') return 1;
    return a[0].localeCompare(b[0]);
  }));
}

export function FileTree({ moduleId, entry }: FileTreeProps) {
  const [tree, setTree] = useState<FileTreeMap | null>(null);
  const [loading, setLoading] = useState(!entry);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (entry) return;
    let cancelled = false;
    fetch('/feed/file-tree.json')
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => {
        if (cancelled) return;
        setTree(data);
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e.message);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [entry]);

  if (loading) {
    return (
      <div className="file-tree file-tree--loading">
        <p>Loading file tree…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="file-tree file-tree--error">
        <p>Couldn't load file tree: <code>{error}</code></p>
        <p className="file-tree__hint">
          The file tree lives at <code>feed/file-tree.json</code> in the gallery repo.
        </p>
      </div>
    );
  }

  const moduleEntry = entry ?? tree?.[moduleId];
  if (!moduleEntry) {
    return (
      <div className="file-tree file-tree--empty">
        <p>No file tree data for <code>{moduleId}</code>.</p>
        <p className="file-tree__hint">
          The file tree is generated from a snapshot of the gallery repo. If the module
          was added after the last snapshot, it'll show up on the next rebuild.
        </p>
      </div>
    );
  }

  const groups = groupByDirectory(moduleEntry.files, moduleEntry.path);
  const totalSize = moduleEntry.files.reduce((sum, f) => sum + f.size, 0);

  return (
    <div className="file-tree">
      <div className="file-tree__header">
        <div className="file-tree__meta">
          <span className="file-tree__kind">{moduleEntry.kind}</span>
          <span className="file-tree__count">
            {moduleEntry.files.length} {moduleEntry.files.length === 1 ? 'file' : 'files'} · {formatSize(totalSize)}
          </span>
          {moduleEntry.tier && (
            <span className={`file-tree__tier file-tree__tier--${moduleEntry.tier}`}>
              {moduleEntry.tier}
            </span>
          )}
        </div>
        <code className="file-tree__root">{moduleEntry.path}</code>
      </div>

      {moduleEntry.description && (
        <p className="file-tree__description">{moduleEntry.description}</p>
      )}

      <ul className="file-tree__groups">
        {[...groups.entries()].map(([dir, files]) => (
          <li key={dir} className="file-tree__group">
            <div className="file-tree__group-header">
              <span className="file-tree__group-name">
                {dir === '.' ? '(root)' : dir}
              </span>
              <span className="file-tree__group-count">{files.length}</span>
            </div>
            <ul className="file-tree__files">
              {files.map((file) => {
                const filename = file.path.split('/').pop() ?? file.path;
                return (
                  <li key={file.path} className="file-tree__file">
                    <a
                      href={githubUrlFor(moduleEntry, `${moduleEntry.path}${file.path === filename ? filename : file.path}`)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="file-tree__link"
                      title={`Open ${file.path} on GitHub`}
                    >
                      <span className="file-tree__icon">{iconForFile(filename)}</span>
                      <span className="file-tree__name">{filename}</span>
                      <span className="file-tree__size">{formatSize(file.size)}</span>
                    </a>
                  </li>
                );
              })}
            </ul>
          </li>
        ))}
      </ul>
    </div>
  );
}
